import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import Quiz from "@/components/Quiz";
import ConfettiBurst from "@/components/ConfettiBurst";
import LetterCard from "@/components/LetterCard";
import AlphabetLearnScreen from "@/components/AlphabetLearnScreen";
import { getModule } from "@/lib/alphabetModules";
import { buildModuleQuestions } from "@/lib/alphabetQuiz";

import { makeStrictSequence } from "@/lib/quizSequence";
import {
  Minus,
  Plus,
  RotateCcw,
  ArrowLeft,
  BookOpen,
  ArrowRight,
  X,
  GraduationCap,
  ChevronDown,
  PartyPopper,
  Eye,
  PenLine,
} from "lucide-react";
import { t } from "@/i18n";

// Doit rester égal à MINI_JEU_ERREURS dans dinar_economy.py côté backend.
const REWARD_NORMAL = 1;
// Même échelle que MASTERED_BOX côté record_answer.
const MASTERED_BOX = 3;

// Les 4 étapes de lecture, affichées dans le guide repliable en bas de page.
const READING_STEPS = [
  {
    n: "1",
    labelKey: "practicealphabet.etape_reconnais",
    subKey: "practicealphabet.etape_reconnais_sub",
  },
  {
    n: "2",
    labelKey: "practicealphabet.etape_nomme",
    subKey: "practicealphabet.etape_nomme_sub",
  },
  {
    n: "3",
    labelKey: "practicealphabet.etape_prononce",
    subKey: "practicealphabet.etape_prononce_sub",
  },
  {
    n: "4",
    labelKey: "practicealphabet.etape_lis",
    subKey: "practicealphabet.etape_lis_sub",
  },
];

export default function PracticeAlphabet() {
  const navigate = useNavigate();
  const { moduleSlug } = useParams();
  const mod = getModule(moduleSlug);

  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState([]); // groupes cochés en entier
  const [pickedChars, setPickedChars] = useState(() => new Set()); // lettres choisies à la main via le menu déroulant — remplace `selected` quand non vide
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [charStats, setCharStats] = useState({}); // char -> { seen, box, mastered }
  const [count, setCount] = useState(20);
  const [session, setSession] = useState(null);
  const [done, setDone] = useState(null);
  const [mistakes, setMistakes] = useState(0);
  const [learningMode, setLearningMode] = useState("both");
  const [notice, setNotice] = useState("");
  const [mastered, setMastered] = useState([]); // chars maîtrisés (booléen, pour les en-têtes de groupe)
  const [showLearn, setShowLearn] = useState(true); // activé d'office
  const [learnQueue, setLearnQueue] = useState(null);
  const [hintLetter, setHintLetter] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const [retryKey, setRetryKey] = useState(0); // force Quiz à repartir de zéro sur "Recommencer"

  // Changement de module : on repart d'un écran de réglages propre.
  useEffect(() => {
    setSelected([]);
    setPickedChars(new Set());
    setExpandedGroupId(null);
    setCharStats({});
    setSession(null);
    setLearnQueue(null);
    setDone(null);
    setNotice("");
    setLearningMode("both");
  }, [mod.slug]);

  // Module 1 : groupes servis par l'API. Modules 2 à 4 : contenu statique.
  useEffect(() => {
    if (!mod.remote) {
      setGroups(mod.groups || []);
      return;
    }
    api
      .get("/content/alphabet")
      .then((r) => setGroups(r.data?.groups || []))
      .catch(() => setGroups([]));
  }, [mod]);

  useEffect(() => {
    if (mod.remote) {
      api
        .get("/practice/alphabet/mistakes")
        .then((r) => setMistakes(r.data?.count || 0))
        .catch(() => {});
    } else {
      setMistakes(0);
    }
    api
      .get("/progression/alphabet")
      .then((r) => {
        const letters = r.data?.letters || [];
        const stats = {};
        const masteredChars = [];
        for (const l of letters) {
          stats[l.char] = {
            seen: l.seen || 0,
            box: l.box || 0,
            mastered: !!l.mastered,
          };
          if (l.mastered) masteredChars.push(l.char);
        }
        setCharStats(stats);
        setMastered(masteredChars);
      })
      .catch(() => {
        setCharStats({});
        setMastered([]);
      });
    setExpandedGroupId(null);
  }, [done, mod]);

  // Index char -> détails, pour les fiches
  const letterByChar = useMemo(() => {
    const m = {};
    for (const g of groups)
      for (const l of g.letter_details || []) m[l.char] = l;
    return m;
  }, [groups]);

  const playAudio = (kind, char) => {
    const a = new Audio(
      `/api/content/alphabet/audio/${kind}/${encodeURIComponent(char)}`,
    );
    a.play().catch(() => {});
  };

  const toggle = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );
  const all = () =>
    setSelected(
      selected.length === groups.length ? [] : groups.map((g) => g.id),
    );

  const toggleChar = (c) =>
    setPickedChars((s) => {
      const next = new Set(s);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const toggleExpandGroup = (gid, e) => {
    e.stopPropagation();
    if (expandedGroupId === gid) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(gid);
  };

  const cleanQuestions = (qs) =>
    qs.map((q) => {
      const safeQ = { ...q, skill: "alphabet" };
      delete safeQ.subtext;
      delete safeQ.subtitle;
      delete safeQ.hint;
      delete safeQ.help;
      delete safeQ.translit;
      delete safeQ.name;
      delete safeQ.sub;
      return safeQ;
    });

  // Toutes les lettres de la sélection (vues ou non) : coché, on montre
  // la fiche de chacune avant de commencer — pas seulement celles
  // jamais rencontrées.
  const lettersFor = (qs) => {
    const chars = [...new Set(qs.map((q) => q.char).filter(Boolean))];
    return chars.map((c) => letterByChar[c]).filter(Boolean);
  };

  // Sélection effective : les lettres choisies à la main remplacent la
  // sélection par groupe dès qu'il y en a au moins une.
  const selectedLetters = () => {
    if (pickedChars.size > 0) {
      const all = groups.flatMap((g) => g.letter_details || []);
      return all.filter((l) => pickedChars.has(l.char));
    }
    return groups
      .filter((g) => selected.includes(g.id))
      .flatMap((g) => g.letter_details || []);
  };

  const launch = (qs) => {
    const strict = cleanQuestions(makeStrictSequence(qs));
    const toLearn = showLearn ? lettersFor(strict) : [];
    setDone(null);
    setRetryKey((k) => k + 1);
    if (toLearn.length) {
      setLearnQueue({ letters: toLearn, questions: strict });
      setSession(null);
    } else {
      setLearnQueue(null);
      setSession(strict);
    }
  };

  // Modules 2 à 4 : questions générées localement à partir du contenu
  // statique — pas d'appel réseau, tout est déjà connu côté client.
  const startStatic = () => {
    const letters = selectedLetters();
    if (!letters.length) {
      setNotice(t("practicealphabet.aucun_element_selection"));
      return;
    }
    const questions = buildModuleQuestions(
      mod,
      groups,
      selected,
      learningMode,
      count,
      pickedChars.size > 0 ? pickedChars : null,
    );
    if (!questions.length) {
      setNotice(t("practicealphabet.selectionne_groupe_ou_lettre"));
      return;
    }
    launch(questions);
  };

  const start = async () => {
    setNotice("");
    if (!mod.remote) return startStatic();
    try {
      const filterParam =
        pickedChars.size > 0
          ? `chars=${encodeURIComponent([...pickedChars].join(","))}`
          : `groups=${selected.join(",")}`;
      let questions = [];
      if (learningMode === "both") {
        const halfName = Math.ceil(count / 2);
        const halfSound = Math.floor(count / 2);
        const [resName, resSound] = await Promise.all([
          api.get(
            `/practice/alphabet?count=${halfName}&${filterParam}&mode=name`,
          ),
          api.get(
            `/practice/alphabet?count=${halfSound}&${filterParam}&mode=sound`,
          ),
        ]);
        const arr1 = resName.data?.questions || [];
        const arr2 = resSound.data?.questions || [];
        const max = Math.max(arr1.length, arr2.length);
        for (let i = 0; i < max; i++) {
          if (arr1[i]) questions.push(arr1[i]);
          if (arr2[i]) questions.push(arr2[i]);
        }
        questions = questions.slice(0, count);
      } else {
        const { data } = await api.get(
          `/practice/alphabet?count=${count}&${filterParam}&mode=${learningMode}`,
        );
        questions = data?.questions || [];
      }
      if (!questions.length) {
        setNotice(t("practicealphabet.aucune_question_selection"));
        return;
      }
      launch(questions);
    } catch {
      setNotice(t("common.seance_echec"));
    }
  };

  const backToSettings = () => {
    setSession(null);
    setLearnQueue(null);
    setDone(null);
    setConfirmExit(false);
    setCharStats({});
    setExpandedGroupId(null);
  };

  const requestExit = () => setConfirmExit(true);

  // Styles de mise en page partagés par les écrans de cette page.
  const PageStyles = (
    <style>{`
        .alpha-actionbar {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 30;
          padding: 0.75rem 1rem
                calc(var(--nav-bottom-mobile, 3.5rem) + env(safe-area-inset-bottom, 0px) + 0.5rem);
        }
        @media (min-width: 768px) {
          .alpha-actionbar { padding-bottom: 1rem; }
        }
        .alpha-actionbar { background: hsl(var(--background)); }
        @supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
          .alpha-actionbar {
            background: hsl(var(--background) / 0.95);
            -webkit-backdrop-filter: blur(16px);
            backdrop-filter: blur(16px);
          }
        }
        .alpha-page {
          padding-bottom: calc(var(--nav-bottom-mobile, 3.5rem) + env(safe-area-inset-bottom, 0px) + 8rem);
        }
        @media (min-width: 768px) {
          .alpha-page { padding-bottom: 9rem; }
        }
      `}</style>
  );

  // ── Écran d'apprentissage ──
  if (learnQueue) {
    return (
      <>
        <AlphabetLearnScreen
          letters={learnQueue.letters}
          onAudio={playAudio}
          onDone={() => {
            setSession(learnQueue.questions);
            setLearnQueue(null);
          }}
          onExit={requestExit}
        />
        {confirmExit && (
          <ExitConfirm
            onCancel={() => setConfirmExit(false)}
            onConfirm={backToSettings}
          />
        )}
      </>
    );
  }

  // ── Quiz ──
  if (session && !done) {
    return (
      <>
        <Quiz
          key={retryKey}
          questions={session}
          onDone={(r) => setDone(r)}
          rewardMode="normal"
          rewardAmount={REWARD_NORMAL}
          onHint={(q) => setHintLetter(letterByChar[q.char] || null)}
          showCelebration={false}
          title={mod.title}
          onExit={backToSettings}
        />
        {hintLetter && (
          <div
            className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setHintLetter(null)}
          >
            <div
              className="w-full max-w-sm rounded-3xl border bg-card p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-end mb-1">
                <button
                  onClick={() => setHintLetter(null)}
                  className="rounded-full p-1.5 hover:bg-muted"
                >
                  <X size={17} />
                </button>
              </div>
              <LetterCard letter={hintLetter} onAudio={playAudio} />
              <button
                onClick={() => setHintLetter(null)}
                className="w-full mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-bold"
              >
                {t("exerciseblock.j_ai_compris")}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── Fin de session : dinars + stats + navigation, un seul écran ──
  if (done) {
    const score = Math.round((done.correct / done.total) * 100);
    const passed = score >= 70;
    const earned = done.earned ?? 0;
    return (
      <>
        {PageStyles}
        <div
          className="alpha-page max-w-xl mx-auto text-center px-4"
          data-testid="alpha-result"
        >
          <div className="rounded-3xl border bg-card p-6 sm:p-7 relative overflow-hidden">
            {earned > 0 && <ConfettiBurst />}
            <PartyPopper size={44} className="mx-auto mb-3 text-amber-500" />
            <h2 className="font-head text-2xl font-bold mb-1">
              {t("quiz.exercice_termine")}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {done.correct} / {done.total} {t("myerrors.bonnes_reponses")}{" "}
              {score}%
            </p>
            <div className="text-5xl font-head font-extrabold text-amber-500 mb-1">
              +{earned} 🪙
            </div>
            <p className="text-xs text-muted-foreground mb-6">
              {earned > 0
                ? t("quiz.gagnes_sur_cet_exercice")
                : t("quiz.aucun_dinar_cette_fois_retente")}
            </p>
            <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-6">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${score}%`,
                  backgroundColor: passed
                    ? "#16A34A"
                    : "hsl(var(--destructive))",
                }}
              />
            </div>
            <button
              onClick={backToSettings}
              className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold mb-2.5"
            >
              {t("exerciseblock.continuer")}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setDone(null);
                  setRetryKey((k) => k + 1);
                  setSession(session);
                }}
                className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
              >
                <RotateCcw size={15} /> {t("myerrors.recommencer")}
              </button>
              <button
                onClick={() => navigate("/practice/alphabet")}
                className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
              >
                <ArrowLeft size={15} /> {t("practicealphabet.modules")}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Réglages ──
  const selectedChars =
    pickedChars.size > 0
      ? [...pickedChars]
      : groups
          .filter((g) => selected.includes(g.id))
          .flatMap((g) => (g.letter_details || []).map((l) => l.char));
  const masteredSel = selectedChars.filter((c) => mastered.includes(c)).length;
  const modes = mod.modes || [];

  return (
    <>
      {PageStyles}
      <div className="alpha-page max-w-2xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-1">
          <button
            onClick={() => navigate("/practice/alphabet")}
            data-testid="back-modules"
            className="rounded-xl border p-2 hover:bg-secondary transition shrink-0"
            aria-label={t("practicealphabet.retour_aux_modules")}
          >
            <ArrowLeft size={16} />
          </button>
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground grid place-items-center text-[11px] font-bold shrink-0">
            {mod.n}
          </span>
          <h1 className="font-head text-2xl font-bold truncate">{mod.title}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">{mod.intro}</p>

        {mistakes > 0 && (
          <button
            onClick={() => navigate("/my-errors")}
            data-testid="review-mistakes"
            className="w-full mb-4 rounded-2xl border-2 border-destructive/40 bg-destructive/5 p-3.5 flex items-center justify-between gap-3 text-left"
          >
            <div className="min-w-0">
              <div className="font-semibold text-sm">
                {mistakes} {t("practicealphabet.lettre_s_a_revoir")}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("practicealphabet.retrouve_les_dans_tes_erreurs")}
              </div>
            </div>
            <span className="shrink-0 rounded-xl bg-destructive text-destructive-foreground px-4 py-2 text-xs font-bold whitespace-nowrap">
              {t("practicealphabet.voir_mes_erreurs")}
            </span>
          </button>
        )}

        {notice && (
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3 mb-4 text-sm text-amber-800 dark:text-amber-300">
            {notice}
          </div>
        )}

        {/* Réglages compacts */}
        <div className="rounded-2xl border bg-card p-4 mb-4 space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
              {t("lessoneditor.competence")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div
                data-testid="skill-read"
                className="rounded-xl border-2 border-primary bg-primary/10 p-2.5 flex items-center justify-center gap-2"
              >
                <Eye size={15} className="text-primary" />
                <span className="text-sm font-semibold">
                  {t("practicealphabet.lire")}
                </span>
              </div>
              <button
                type="button"
                disabled
                data-testid="skill-write"
                className="relative rounded-xl border-2 p-2.5 flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
              >
                <PenLine size={15} />
                <span className="text-sm font-semibold">
                  {t("practicealphabet.ecrire")}
                </span>
                <span className="absolute -top-2 right-1.5 rounded-full bg-secondary border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">
                  {t("practice.bientot")}
                </span>
              </button>
            </div>
          </div>

          {modes.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
                {t("practicealphabet.quoi_apprendre")}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {modes.map(([id]) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`alphabet-mode-${id}`}
                    onClick={() => setLearningMode(id)}
                    className={`rounded-xl border-2 p-2.5 text-center transition ${
                      learningMode === id
                        ? "border-primary bg-primary/10"
                        : "hover:border-primary/40"
                    }`}
                  >
                    <div className="text-sm font-semibold leading-tight">
                      {t(`practicealphabet.mode_${id}`)}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {t(`practicealphabet.mode_${id}_sub`)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium">
              {t("practicealphabet.questions")}
            </span>
            <div className="flex items-center gap-2">
              <button
                data-testid="count-minus"
                onClick={() => setCount((c) => Math.max(5, c - 5))}
                className="w-9 h-9 rounded-lg border grid place-items-center"
              >
                <Minus size={15} />
              </button>
              <span
                className="font-head text-lg font-bold w-8 text-center tabular-nums"
                data-testid="count-value"
              >
                {count}
              </span>
              <button
                data-testid="count-plus"
                onClick={() => setCount((c) => c + 5)}
                className="w-9 h-9 rounded-lg border grid place-items-center"
              >
                <Plus size={15} />
              </button>
            </div>
          </div>

          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm">
              <span className="font-medium">
                {t("myerrors.voir_les_fiches_avant")}
              </span>
              <span className="block text-xs text-muted-foreground">
                {t("practicealphabet.une_fiche_pour_chaque_element_de")}
              </span>
            </span>
            <input
              type="checkbox"
              checked={showLearn}
              onChange={(e) => setShowLearn(e.target.checked)}
              className="w-5 h-5 accent-primary shrink-0"
            />
          </label>
        </div>

        {/* Groupes + sélection lettre par lettre */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {mod.remote
              ? t("practicealphabet.groupes_de_lettres")
              : t("practicealphabet.groupes")}
          </span>
          <button
            data-testid="select-all"
            onClick={all}
            className="text-sm text-primary hover:underline"
          >
            {selected.length === groups.length
              ? t("practicealphabet.aucun")
              : t("practicealphabet.tout")}
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mb-2">
          {t("practicealphabet.touche_un_groupe_entier_ou_la")}
        </p>

        {pickedChars.size > 0 && (
          <div className="rounded-2xl border border-primary/40 bg-primary/5 p-3 mb-3 flex items-center justify-between gap-3">
            <span className="text-sm font-medium">
              {t("practicealphabet.elements_choisis", {
                count: pickedChars.size,
                element:
                  pickedChars.size > 1
                    ? t("practicealphabet.elements_pluriel")
                    : t("practicealphabet.element_singulier"),
                s: pickedChars.size > 1 ? "s" : "",
              })}
            </span>
            <button
              onClick={() => setPickedChars(new Set())}
              className="text-sm text-primary hover:underline shrink-0"
            >
              {t("practicealphabet.reinitialiser")}
            </button>
          </div>
        )}

        <div className="space-y-2 mb-4">
          {groups.map((g) => {
            const chars = (g.letter_details || []).map((l) => l.char);
            const ok = chars.filter((c) => mastered.includes(c)).length;
            const pct = chars.length
              ? Math.round((100 * ok) / chars.length)
              : 0;
            const on = selected.includes(g.id);
            const expanded = expandedGroupId === g.id;
            const chosenHere = chars.filter((c) => pickedChars.has(c)).length;

            return (
              <div
                key={g.id}
                className={`rounded-2xl border-2 overflow-hidden transition bg-card ${
                  on ? "bg-amber-500/10" : "hover:border-amber-500/40"
                }`}
                style={on ? { borderColor: "#CA8A04" } : {}}
              >
                <div className="flex items-stretch">
                  <button
                    data-testid={`group-${g.id}`}
                    onClick={() => toggle(g.id)}
                    className="flex-1 min-w-0 text-left p-3"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="font-medium text-sm truncate">
                        {g.label}
                      </span>
                      <span className="text-[11px] font-bold text-muted-foreground shrink-0 tabular-nums">
                        {pct}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-1.5">
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                      <span>
                        {ok}/{chars.length} {t("myerrors.maitrises")}
                      </span>
                      {chosenHere > 0 && (
                        <span className="text-primary font-semibold">
                          {t("practicealphabet.n_choisis", {
                            count: chosenHere,
                            s: chosenHere > 1 ? "s" : "",
                          })}
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={(e) => toggleExpandGroup(g.id, e)}
                    aria-label={t("practicealphabet.voir_le_detail_du_groupe")}
                    data-testid={`expand-${g.id}`}
                    className="shrink-0 w-10 grid place-items-center border-l hover:bg-secondary/20 transition"
                  >
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>

                {expanded && (
                  <div className="border-t p-2 space-y-1.5 max-h-72 overflow-y-auto">
                    {(g.letter_details || []).map((l) => {
                      const stat = charStats[l.char] || {
                        seen: 0,
                        box: 0,
                        mastered: false,
                      };
                      return (
                        <label
                          key={l.char}
                          className="flex items-center gap-2 rounded-xl p-2 hover:bg-secondary/40 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-primary shrink-0"
                            checked={pickedChars.has(l.char)}
                            onChange={() => toggleChar(l.char)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span
                                className="arabic text-xl truncate"
                                dir="rtl"
                              >
                                {l.char}
                              </span>
                              <span className="text-[10px] text-muted-foreground shrink-0 text-right">
                                {stat.mastered
                                  ? t("practicealphabet.maitrise")
                                  : `${stat.box}/${MASTERED_BOX}`}
                                <br />
                                {t("practicealphabet.vu")} {stat.seen}{" "}
                                {t("practicealphabet.fois")}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {l.name}
                            </div>
                            <div className="h-1 rounded-full bg-secondary overflow-hidden mt-1">
                              <div
                                className="h-full rounded-full bg-emerald-500"
                                style={{
                                  width: `${Math.min(100, (stat.box / MASTERED_BOX) * 100)}%`,
                                }}
                              />
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Explications, repliées en bas */}
        <button
          onClick={() => setShowGuide((s) => !s)}
          className="w-full rounded-2xl border bg-card p-3.5 flex items-center justify-between gap-2 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-semibold">
            <GraduationCap size={16} className="text-primary" />{" "}
            {t("practicealphabet.comment_apprendre_a_lire")}
          </span>
          <ChevronDown
            size={16}
            className={`transition-transform shrink-0 ${showGuide ? "rotate-180" : ""}`}
          />
        </button>
        {showGuide && (
          <div className="rounded-2xl border bg-card p-4 mt-2 space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("practicealphabet.lire_un_mot_arabe_demande_quatre")}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {READING_STEPS.map((step) => (
                <div key={step.n} className="rounded-xl bg-secondary/50 p-2.5">
                  <div className="text-xs font-bold">
                    {step.n}. {t(step.labelKey)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {t(step.subKey)}
                  </div>
                </div>
              ))}
            </div>
            <button
              data-testid="open-alphabet-cheatsheet"
              onClick={() => navigate("/cheatsheets?level=Base")}
              className="w-full py-3 rounded-xl border font-semibold text-sm flex items-center justify-center gap-2"
            >
              <BookOpen size={15} />{" "}
              {t("practicealphabet.ouvrir_la_fiche_memo")}
            </button>
          </div>
        )}
      </div>

      {/* Bouton toujours visible, au-dessus de la barre d'onglets mobile */}
      <div className="alpha-actionbar border-t">
        <div className="max-w-2xl mx-auto">
          {selectedChars.length > 0 && (
            <div className="text-[11px] text-muted-foreground text-center mb-1.5 tabular-nums">
              {masteredSel}/{selectedChars.length}{" "}
              {t("practicealphabet.elements_maitrises")}
            </div>
          )}
          <button
            data-testid="start-alpha"
            disabled={selected.length === 0 && pickedChars.size === 0}
            onClick={start}
            className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {pickedChars.size > 0
              ? t("practicealphabet.commencer_avec_n_elements", {
                  count: pickedChars.size,
                  s: pickedChars.size > 1 ? "s" : "",
                })
              : selected.length === 0
                ? t("practicealphabet.choisis_un_groupe")
                : t("practicealphabet.commencer")}{" "}
            <ArrowRight size={17} />
          </button>
        </div>
      </div>
    </>
  );
}

/* Confirmation avant de quitter un exercice en cours : la progression
      de la séance (pas la maîtrise déjà acquise avant) sera perdue. */
function ExitConfirm({ onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-3xl border bg-card p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-head text-lg font-bold mb-2">
          {t("quiz.quitter_l_exercice")}
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          {t("practicealphabet.ta_progression_sur_cette_seance_sera")}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onCancel}
            className="py-3 rounded-2xl border font-semibold"
          >
            {t("exerciseblock.continuer")}
          </button>
          <button
            onClick={onConfirm}
            className="py-3 rounded-2xl bg-destructive text-destructive-foreground font-semibold"
          >
            {t("alphabetlearnscreen.quitter")}
          </button>
        </div>
      </div>
    </div>
  );
}